import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { FirebaseApp } from 'firebase/app';
import {
    Auth,
    FacebookAuthProvider,
    GoogleAuthProvider,
    OAuthProvider,
    User,
    UserCredential,
    getAuth,
    getRedirectResult,
    isSignInWithEmailLink,
    signInWithEmailLink,
    signInWithPopup,
    signInWithRedirect,
    signOut,
} from 'firebase/auth';
import './styles.scss';
import { isValidEmail, verifyIdToken, sendEmailLink, getKnownUser, resolveEmailLink } from './util';

declare global {
    interface Window {
        firebasePromise: Promise<FirebaseApp>;
    }
}

type PhoenixLoginConfig = {
    addUtmSource?: boolean;
    authConfig?: AuthConfig;
    siteId: string;
    siteKeyword: string;
    taigaBaseUrl: string;
    ui?: {
        signInFlow?: 'popup' | 'redirect';
        providers?: string[];
    };
};

type AuthConfig = {
    authTheme?: AuthTheme;
};

type AuthTheme = {
    logo: string;
};

const EMAIL_KEY = 'phoenix:login:email';
const TARGET_URL_KEY = 'phoenix:login:targetUrl';

@customElement('phoenix-login')
export default class PhoenixLogin extends LitElement {
    private auth?: Auth;

    @property({ type: Object })
    public config!: PhoenixLoginConfig;

    @state()
    private message: string;

    @state()
    private email: string;

    @state()
    private emailLinkPhase: null | 'input' | 'sent' | 'confirm';

    @state()
    private oauthPhase: null | 'choose';

    @state()
    private isLoading: boolean;

    constructor() {
        super();
        this.message = '';
        this.email = '';
        this.emailLinkPhase = null;
        this.oauthPhase = null;
        this.isLoading = false;
    }

    connectedCallback(): void {
        super.connectedCallback();

        void window.firebasePromise
            .then((firebaseApp) => {
                this.auth = getAuth(firebaseApp);
                if (isSignInWithEmailLink(this.auth, window.location.href)) {
                    void this.continueEmailSignIn();
                } else {
                    this.regularSignIn();
                }
            })
            .catch((error) => {
                // eslint-disable-next-line no-console
                console.error(error);
            });
    }

    async continueEmailSignIn() {
        if (!this.auth) return;

        // To prevent a sign-in link from being used to sign in as an unintended user or on an unintended
        // device, Firebase Auth requires the user's email address to be provided when completing the sign-in
        // flow. For sign-in to succeed, this email address must match the address to which the sign-in link
        // was originally sent.
        let email = localStorage.getItem(EMAIL_KEY);

        if (!email) {
            const searchParams = new URLSearchParams(window.location.search);
            const sid = searchParams.get('sid');

            if (!sid) {
                this.emailLinkPhase = 'confirm';
                return;
            }

            const oobCode = searchParams.get('oobCode');
            if (!oobCode) {
                this.message = 'An error occurred during the sign-in process.';
                this.emailLinkPhase = null;
                return;
            }

            try {
                email = await resolveEmailLink(sid, oobCode);
            } catch (error) {
                this.message = `An error occurred during the sign-in process. ${
                    error instanceof Error ? error.message : String(error)
                }`;
                this.emailLinkPhase = null;
                return;
            }
        }

        // If there is no email address in local storage, for example because the link is being used in a
        // different browser than the one that requested the link, the user is asked to enter it.
        if (!email) {
            this.emailLinkPhase = 'confirm';
            return;
        }

        void this.confirmEmail(this.auth, email);
    }

    regularSignIn() {
        if (!this.auth) return;

        this.emailLinkPhase = 'input';
        this.oauthPhase = 'choose';

        // The getRedirectResult function below takes time to return results if the page load is the last step
        // of the redirect auth flow. So, we will display the loading screen before displaying the sign-in
        // options so that it does not make the user feel as if nothing is happening.
        this.isLoading = true;

        getRedirectResult(this.auth)
            .then(async (result) => {
                if (result) {
                    await this.handleSignInSuccess(result);
                } else {
                    localStorage.setItem(
                        TARGET_URL_KEY,
                        new URLSearchParams(window.location.search).get('targetUrl') ?? ''
                    );
                }
            })
            .catch((error) => this.handleSignInError(error))
            .finally(() => {
                this.isLoading = false;
            });
    }

    // https://github.com/themaven-net/web-chilliwack/blob/b056e2879b5a63541edd2f8a820bd6186d35e728/src/components/SyncLogin/EmailInputView.tsx#L57-L60
    disableNext() {
        return this.email.indexOf('@') > 0 && !this.email.endsWith('@');
    }

    // Bind input field to the email property.
    inputEmailHandler(event: Event) {
        if (event.target instanceof HTMLInputElement) {
            this.email = event.target.value;
        }
    }

    async handleContinueClick(event: Event) {
        event.preventDefault();

        if (!isValidEmail(this.email)) {
            this.message = 'Invalid email address';
            return;
        }
        this.message = '';

        // The user must return to the login page that has this component once before returning directly to
        // the page they were originally on.
        const loginUrl = `${window.location.protocol}//${window.location.host}${window.location.pathname}`;
        localStorage.setItem(EMAIL_KEY, this.email);
        try {
            await sendEmailLink(this.config.taigaBaseUrl, this.email, this.config.siteId, loginUrl);
            this.emailLinkPhase = 'sent';
            this.oauthPhase = null;
        } catch (error) {
            this.message = `Failed to send email: ${error as string}`;
        }
    }

    async handleEmailConfirmClick(event: Event) {
        event.preventDefault();
        void this.confirmEmail(this.auth!, this.email);
    }

    async handleSignInSuccess(result: UserCredential) {
        this.oauthPhase = null;
        this.message = 'Signing in...';

        this.completeLogin(this.auth!, result.user!).catch((error) => {
            this.message = `An error occurred during the sign-in process. ${error as string}`;
            this.oauthPhase = 'choose';
        });
    }

    // https://github.com/firebase/firebaseui-web/blob/44884c70d4c67e52a918037dd841a16419fab94c/javascript/widgets/handler/common.js#L51-L80
    normalizeError(error: any) {
        if (
            error['code'] === 'auth/invalid-credential' &&
            error['message'] &&
            error['message'].indexOf('error=consent_required') !== -1
        ) {
            return { code: 'auth/user-cancelled' };
        } else if (
            error['message'] &&
            error['message'].indexOf('HTTP Cloud Function returned an error:') !== -1
        ) {
            const firstIndex = error['message'].indexOf('{');
            const lastIndex = error['message'].lastIndexOf('}');
            const errorObject = JSON.parse(error['message'].substring(firstIndex, lastIndex + 1));
            const newErrorMessage = errorObject && errorObject['error'] && errorObject['error']['message'];
            return { code: error['code'], message: newErrorMessage || error['message'] };
        }
        return error;
    }

    handleSignInError(error: any) {
        error = this.normalizeError(error);

        let message = `An error occurred during the sign-in process. ${error.message}`;

        switch (error.code) {
            case 'auth/account-exists-with-different-credential':
                message =
                    'An account already exists with the same email address but with a different sign-in method. Please sign in through the email field below.';
                window.reportErrorToPetametrics(`Login failed. ${message}`, error);
                break;
            case 'auth/internal-error':
                message = error.message;
                window.reportErrorToPetametrics(`Login failed. ${message}`, error);
                break;
        }

        this.message = message;
    }

    async signIn(providerId: string) {
        let provider;
        switch (providerId) {
            case 'google':
                provider = new GoogleAuthProvider();
                break;
            case 'facebook':
                provider = new FacebookAuthProvider();
                break;
            case 'apple':
                provider = new OAuthProvider('apple.com');
                break;
            case 'microsoft':
                provider = new OAuthProvider('microsoft.com');
                break;
            default:
                // eslint-disable-next-line no-console
                console.log(`Unknown providerId: ${providerId}`);
                return;
        }

        provider.addScope('email');

        if (this.config.ui?.signInFlow === 'popup') {
            signInWithPopup(this.auth!, provider)
                .then(async (result) => this.handleSignInSuccess(result))
                .catch((error) => this.handleSignInError(error));
        } else {
            await signInWithRedirect(this.auth!, provider);
        }
    }

    async confirmEmail(auth: Auth, email: string): Promise<void> {
        this.message = 'Signing in...';

        void signInWithEmailLink(this.auth!, email, window.location.href)
            .then(async (userCredential) => {
                try {
                    await this.completeLogin(auth!, userCredential.user!);
                } catch (error) {
                    this.message = `An error occurred during the sign-in process. ${error as string}`;
                    this.emailLinkPhase = 'confirm';
                }

                localStorage.removeItem(EMAIL_KEY);
            })
            .catch((error) => {
                if (error.code === 'auth/invalid-action-code') {
                    this.message =
                        'The action code is invalid. This can happen if the code is malformed, expired, or has already been used.';
                    this.emailLinkPhase = null;
                } else if (error.code === 'auth/invalid-email') {
                    this.message = 'The email provided does not match the sign-in email address.';
                } else {
                    this.message = `An error occurred during the sign-in process. ${error.message ?? error}`;
                }
            });
    }

    async completeLogin(auth: Auth, user: User): Promise<void> {
        const idToken = await user.getIdToken();

        // We may want to keep the Firebase user logged-in for something in the future, but
        // for now we don't need it once we get the token.
        void signOut(auth);

        const result = await verifyIdToken(idToken, this.config.siteKeyword);
        const knownUser = await getKnownUser(this.config.taigaBaseUrl, result.tokens.RGIS);
        const userRawEmail = knownUser.userProfile.email;

        if (userRawEmail && window.lockrAIM?.setAdditionalData) {
            window.lockrAIM.setAdditionalData({
                type: 'email',
                data: userRawEmail,
            });
        }

        if (result.emailHash && window.permutive) {
            await window.permutive.identify([
                {
                    id: result.emailHash,
                    tag: 'email_sha256',
                },
            ]);
        }

        this.message = 'Signed in. Redirecting...';

        const storedTargetUrl = localStorage.getItem(TARGET_URL_KEY);
        localStorage.removeItem(TARGET_URL_KEY);
        let target = new URL(storedTargetUrl || window.location.origin);
        // Validate the target URL to prevent open redirect vulnerabilities.
        if (target.origin !== window.location.origin) {
            target = new URL(window.location.origin);
        }
        target.hash = `${target.hash ? '&' : '#'}viaLogin`;

        if (this.config.addUtmSource) {
            target.searchParams.set('source', 'vialogin');
        }

        window.location.href = target.href;
    }

    render() {
        return html`<div class="m-phoenix-login">
            <div>
                ${this.config?.authConfig?.authTheme?.logo
                    ? html`
                          <img
                              class="m-phoenix-login--logo"
                              src="${this.config?.authConfig?.authTheme?.logo}"
                          />
                      `
                    : html` <phoenix-icon-fallback class="m-svg" icon="arena_group_logo" aria-hidden="true">
                          <svg>
                              <use xlink:href="/assets-global/icons/svg-sprite.svg#arena_group_logo"></use>
                          </svg>
                      </phoenix-icon-fallback>`}
            </div>
            <div class="m-phoenix-login--loading" ?hidden="${!this.isLoading}">Loading...</div>
            ${this.message ? html`<p class="m-phoenix-login--message">${this.message}</p>` : html``}
            <div ?hidden="${this.isLoading}">
                <div class="m-phoenix-login--email">
                    <div class="m-phoenix-login--email-input" ?hidden="${this.emailLinkPhase !== 'input'}">
                        <p>CONTINUE WITH EMAIL</p>
                        <form @submit="${this.handleContinueClick}">
                            <div>
                                <input
                                    type="email"
                                    placeholder="Email Address"
                                    .value="${this.email}"
                                    @input="${this.inputEmailHandler}"
                                />
                            </div>
                            <div>
                                <button type="submit" ?disabled="${!this.disableNext()}">
                                    CONTINUE &rsaquo;
                                </button>
                            </div>
                        </form>
                    </div>
                    <div class="m-phoenix-login--email-sent" ?hidden="${this.emailLinkPhase !== 'sent'}">
                        <p><strong>Sign-in email sent</strong></p>
                        <p class="m-phoenix-login--email-sent-message">
                            A sign-in email with additional instructions was sent to
                            <strong>${this.email}</strong>. Check your email to complete sign-in.
                        </p>
                    </div>
                    <div
                        class="m-phoenix-login--email-confirm"
                        ?hidden="${this.emailLinkPhase !== 'confirm'}"
                    >
                        <p>Please provide your email for confirmation</p>
                        <form @submit="${this.handleEmailConfirmClick}">
                            <input
                                type="email"
                                placeholder="Email Address"
                                .value="${this.email}"
                                @input="${this.inputEmailHandler}"
                            /><button type="submit" ?disabled="${!this.disableNext()}">
                                CONFIRM &rsaquo;
                            </button>
                        </form>
                    </div>
                </div>
                <div ?hidden="${!this.oauthPhase}">
                    <p>OR</p>
                    <div class="m-phoenix-login--container">
                        ${this.config.ui?.providers?.map(
                            (provider) => html`
                                <button
                                    @click="${() => this.signIn(provider)}"
                                    class="
                m-phoenix-login--idp-button m-phoenix-login--idp-${provider}"
                                >
                                    <phx-svg-icon
                                        icon="ico_${provider}"
                                        class="m-phoenix-login--idp-icon"
                                    ></phx-svg-icon
                                    ><span>Sign in with </span>
                                    <span class="m-phoenix-login--idp-text">${provider}</span>
                                </button>
                            `
                        )}
                    </div>
                </div>
                <div class="m-phoenix-login--footer" ?hidden="${this.emailLinkPhase == 'sent'}">
                    <p>
                        Signing up for a Pro account will add you to our newsletter. By continuing you agree
                        to The Street Pro's
                        <a
                            href="https://www.thestreetpro.com/privacy-policy"
                            onclick="return phoenixTrackClickEvent(this, event);"
                            rel="noopener"
                            target="_blank"
                            >Privacy Policy</a
                        >
                        and
                        <a
                            href="https://www.thestreetpro.com/terms"
                            onclick="return phoenixTrackClickEvent(this, event);"
                            rel="noopener"
                            target="_blank"
                            >Terms of Use</a
                        >.
                    </p>
                </div>
            </div>
        </div>`;
    }

    protected createRenderRoot(): Element | ShadowRoot {
        return this;
    }
}
